import { serviceConnectionsService } from './serviceConnectionsService';
import { configService } from './configService';

export interface ConnectionStatus {
  serviceName: string;
  isConnected: boolean;
  lastCheck: Date;
  lastSuccessfulConnection?: Date;
  consecutiveFailures: number;
  nextRetryAt?: Date;
}

class ConnectionPersistenceService {
  private checkInterval: number | null = null;
  private readonly CHECK_FREQUENCY = 5 * 60 * 1000; // 5 minutos
  private readonly MAX_RETRIES = 3;
  private readonly RETRY_DELAY = 2 * 60 * 1000; // 2 minutos
  private connectionStatuses: Map<string, ConnectionStatus> = new Map();
  private isMonitoring = false;

  /**
   * Inicia el monitoreo automático de conexiones
   */
  startMonitoring(): void {
    if (this.isMonitoring) {
      console.log('⚠️ El monitoreo ya está activo');
      return;
    }

    console.log('🔄 Iniciando monitoreo de conexiones...');
    this.isMonitoring = true;

    // Check immediately
    this.checkAllConnections();

    // Then check periodically
    this.checkInterval = window.setInterval(() => {
      this.checkAllConnections();
    }, this.CHECK_FREQUENCY);

    console.log('✅ Monitoreo de conexiones iniciado');
  }

  /**
   * Detiene el monitoreo automático
   */
  stopMonitoring(): void {
    if (this.checkInterval) {
      clearInterval(this.checkInterval);
      this.checkInterval = null;
      this.isMonitoring = false;
      console.log('⏹️ Monitoreo de conexiones detenido');
    }
  }

  /**
   * Verifica todas las conexiones de servicios
   */
  private async checkAllConnections(): Promise<void> {
    try {
      console.log('🔍 Verificando estado de todas las conexiones...');

      const services = await serviceConnectionsService.getAllServiceConnections();

      for (const service of services) {
        if (service.isActive) {
          await this.checkServiceConnection(service.serviceName);
        }
      }

      console.log('✅ Verificación de conexiones completada');
    } catch (error) {
      console.error('❌ Error verificando conexiones:', error);
    }
  }

  /**
   * Verifica la conexión de un servicio específico
   */
  private async checkServiceConnection(serviceName: string): Promise<void> {
    try {
      const currentStatus = this.connectionStatuses.get(serviceName) || {
        serviceName,
        isConnected: false,
        lastCheck: new Date(),
        consecutiveFailures: 0
      };

      console.log(`🔍 Verificando conexión de ${serviceName}...`);

      let isConnected = false;

      // Test connection based on service type
      switch (serviceName) {
        case 'google':
          isConnected = await this.testGoogleConnection();
          break;
        case 'openai':
          isConnected = await this.testOpenAIConnection();
          break;
        case 'firebase':
          isConnected = await this.testFirebaseConnection();
          break;
        default:
          console.log(`⚠️ Servicio ${serviceName} no soportado para verificación`);
          return;
      }

      const now = new Date();

      if (isConnected) {
        // Connection successful
        console.log(`✅ ${serviceName} conectado correctamente`);

        await serviceConnectionsService.updateLastConnected(serviceName);

        this.connectionStatuses.set(serviceName, {
          serviceName,
          isConnected: true,
          lastCheck: now,
          lastSuccessfulConnection: now,
          consecutiveFailures: 0
        });
      } else {
        // Connection failed
        const newFailureCount = currentStatus.consecutiveFailures + 1;
        console.log(`❌ ${serviceName} desconectado (intento ${newFailureCount}/${this.MAX_RETRIES})`);

        this.connectionStatuses.set(serviceName, {
          serviceName,
          isConnected: false,
          lastCheck: now,
          lastSuccessfulConnection: currentStatus.lastSuccessfulConnection,
          consecutiveFailures: newFailureCount,
          nextRetryAt: new Date(now.getTime() + this.RETRY_DELAY)
        });

        // Log error
        await serviceConnectionsService.logConnectionEvent(
          serviceName,
          'disconnect',
          false,
          `Fallo en verificación automática (intento ${newFailureCount})`
        );

        // Mark as inactive if max retries reached
        if (newFailureCount >= this.MAX_RETRIES) {
          console.log(`⚠️ ${serviceName} marcado como inactivo después de ${this.MAX_RETRIES} intentos fallidos`);
          await serviceConnectionsService.markAsInactive(
            serviceName,
            `Desconectado después de ${this.MAX_RETRIES} intentos fallidos`
          );

          // Send notification (could be integrated with notification system)
          this.notifyConnectionFailure(serviceName);
        } else {
          // Attempt reconnection
          await this.attemptReconnection(serviceName);
        }
      }
    } catch (error) {
      console.error(`❌ Error verificando ${serviceName}:`, error);
    }
  }

  /**
   * Intenta reconectar un servicio
   */
  private async attemptReconnection(serviceName: string): Promise<void> {
    try {
      console.log(`🔄 Intentando reconectar ${serviceName}...`);

      // Load configuration from database
      const connection = await serviceConnectionsService.getServiceConnection(serviceName);

      if (!connection || !connection.apiKey) {
        console.log(`❌ No hay credenciales para reconectar ${serviceName}`);
        return;
      }

      // Attempt to reconnect based on service
      let reconnected = false;

      switch (serviceName) {
        case 'google':
          reconnected = await this.reconnectGoogle(connection.apiKey, connection.configData);
          break;
        case 'openai':
          reconnected = await this.reconnectOpenAI(connection.apiKey, connection.configData);
          break;
      }

      if (reconnected) {
        console.log(`✅ ${serviceName} reconectado exitosamente`);
        await serviceConnectionsService.logConnectionEvent(
          serviceName,
          'connect',
          true,
          'Reconexión automática exitosa'
        );
        await serviceConnectionsService.updateLastConnected(serviceName);
      } else {
        console.log(`❌ No se pudo reconectar ${serviceName}`);
      }
    } catch (error) {
      console.error(`❌ Error intentando reconectar ${serviceName}:`, error);
    }
  }

  private async testGoogleConnection(): Promise<boolean> {
    try {
      return await configService.testGoogleConnection();
    } catch (error) {
      console.error('Error testing Google:', error);
      return false;
    }
  }

  private async testOpenAIConnection(): Promise<boolean> {
    try {
      return await configService.testOpenAIConnection();
    } catch (error) {
      console.error('Error testing OpenAI:', error);
      return false;
    }
  }

  private async testFirebaseConnection(): Promise<boolean> {
    try {
      return await configService.testFirebaseConnection();
    } catch (error) {
      console.error('Error testing Firebase:', error);
      return false;
    }
  }

  private async reconnectGoogle(apiKey: string, configData: any): Promise<boolean> {
    try {
      configService.saveConfig({
        google: {
          clientId: configData.clientId,
          clientSecret: apiKey,
          redirectUri: configData.redirectUri
        }
      });
      return await configService.testGoogleConnection();
    } catch (error) {
      console.error('Error reconnecting Google:', error);
      return false;
    }
  }

  private async reconnectOpenAI(apiKey: string, configData: any): Promise<boolean> {
    try {
      configService.saveConfig({
        openai: {
          apiKey,
          organizationId: configData.organizationId,
          model: configData.model,
          maxTokens: configData.maxTokens,
          temperature: configData.temperature
        }
      });
      return await configService.testOpenAIConnection();
    } catch (error) {
      console.error('Error reconnecting OpenAI:', error);
      return false;
    }
  }

  /**
   * Notifica sobre una falla de conexión
   */
  private notifyConnectionFailure(serviceName: string): void {
    console.warn(`⚠️ ALERTA: ${serviceName} ha fallado después de múltiples intentos de reconexión`);

    // Could integrate with notification system here
    // For now, just log to console
  }

  /**
   * Obtiene el estado de todas las conexiones
   */
  getConnectionStatuses(): ConnectionStatus[] {
    return Array.from(this.connectionStatuses.values());
  }

  /**
   * Obtiene el estado de una conexión específica
   */
  getConnectionStatus(serviceName: string): ConnectionStatus | null {
    return this.connectionStatuses.get(serviceName) || null;
  }

  /**
   * Fuerza la verificación de un servicio específico
   */
  async forceCheckService(serviceName: string): Promise<void> {
    console.log(`🔄 Forzando verificación de ${serviceName}...`);
    await this.checkServiceConnection(serviceName);
  }

  /**
   * Migra todas las configuraciones de localStorage a base de datos
   */
  async migrateAllConfigurations(): Promise<{ success: number; failed: number }> {
    console.log('🔄 Iniciando migración de configuraciones a base de datos...');

    const result = await serviceConnectionsService.migrateAllFromLocalStorage();

    console.log(`✅ Migración completada: ${result.success} exitosas, ${result.failed} fallidas`);

    return result;
  }
}

export const connectionPersistenceService = new ConnectionPersistenceService();
